import React, { useState, useEffect } from "react";
import {
  Avatar,
  Col,
  Drawer,
  Modal,
  Dropdown,
  Radio,
  Row,
  Switch,
  message,
} from "antd";
import filterIcon from "../../../Assets/filter.svg";
import moment from "moment";
import API_MANAGER from "../../../API";
import { useNavigate } from "react-router-dom";

// Helper function to decode HTML entities
const decodeHtmlEntities = (text) => {
  if (!text) return text;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

// Helper function to get first letter from message (stripping HTML tags)
const getFirstLetter = (message) => {
  if (!message) return "N";
  
  // Decode HTML entities first
  let cleanText = decodeHtmlEntities(message);
  
  // Remove all HTML tags
  cleanText = cleanText.replace(/<[^>]*>/g, '');
  
  // Trim and get first character
  cleanText = cleanText.trim();
  
  if (cleanText.length > 0) {
    const firstChar = cleanText.charAt(0).toUpperCase();
    // Only return if it's a letter or number
    if (/[A-Z0-9]/.test(firstChar)) {
      return firstChar;
    }
  }
  
  return "N";
};

// Helper function to parse message and render clickable links
const renderMessageWithLinks = (messageText, navigate) => {
  if (!messageText) return messageText;
  
  // First decode HTML entities (like &lt; and &gt;)
  let decodedText = decodeHtmlEntities(messageText);
  
  // More robust regex to match anchor tags with any attributes
  // Matches: <a ... href="url" ...>text</a> or <a href="url">text</a>
  const linkRegex = /<a\s+[^>]*href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi;
  const parts = [];
  let lastIndex = 0;
  let match;
  let hasLinks = false;
  
  while ((match = linkRegex.exec(decodedText)) !== null) {
    hasLinks = true;
    // Add text before the link
    if (match.index > lastIndex) {
      const beforeText = decodedText.substring(lastIndex, match.index);
      // Clean any HTML tags from the text before
      const cleanBeforeText = beforeText.replace(/<[^>]*>/g, '');
      if (cleanBeforeText) {
        parts.push(cleanBeforeText);
      }
    }
    
    // Extract URL and link text
    const url = match[1];
    let linkText = match[2];
    
    // Clean HTML tags from link text (in case there are nested tags)
    linkText = linkText.replace(/<[^>]*>/g, '').trim();
    
    if (url && linkText) {
      // Extract path from full URL if it's an internal route
      let routePath = url;
      try {
        const urlObj = new URL(url);
        // Check if it's the same origin
        if (urlObj.origin === window.location.origin || url.includes('/applicant-profile/') || url.includes('/advert-detail/')) {
          routePath = urlObj.pathname + urlObj.search + urlObj.hash;
        }
      } catch (e) {
        // If URL parsing fails, use the original URL
      }
      
      // Add the clickable link
      parts.push(
        <p
          key={`link-${match.index}`}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            // Check if it's an internal route or external URL
            if (routePath.startsWith('/') || routePath.startsWith('#')) {
              // Internal navigation
              if (navigate) {
                navigate(routePath);
              } else {
                window.location.href = routePath;
              }
            } else {
              // External URL
              window.open(url, '_blank', 'noopener,noreferrer');
            }
          }}
          style={{ 
            color: '#071135', 
            textDecoration: 'none',
            cursor: 'pointer'
          }}
        >
          {linkText}
        </p>
      );
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text after the last link
  if (lastIndex < decodedText.length) {
    const afterText = decodedText.substring(lastIndex);
    // Clean any HTML tags from the remaining text
    const cleanAfterText = afterText.replace(/<[^>]*>/g, '');
    if (cleanAfterText) {
      parts.push(cleanAfterText);
    }
  }
  
  // If no links were found, strip all HTML tags and return plain text
  if (!hasLinks) {
    const cleanText = decodedText.replace(/<[^>]*>/g, '').trim();
    return cleanText || messageText;
  }
  
  return parts.length > 0 ? parts : decodedText.replace(/<[^>]*>/g, '');
};

const isRecruiterTransferRequest = (item) =>
  item?.type === "RECRUITER_TRANSFER_REQUEST";

const getTransferStatus = (item) =>
  String(item?.transferStatus || "").toLowerCase();

const getNestedId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  const id = value._id || value.id;
  return id && typeof id !== "object" ? String(id) : "";
};

const getTransferRequestId = (item) =>
  getNestedId(item?.recruiterTransferRequestId) ||
  getNestedId(item?.requestId);

const resolveCompanyLogo = (logo) => {
  if (typeof logo !== "string") return "";
  const value = logo.trim();
  if (!value) return "";
  if (value.startsWith("http")) return value;
  return `https://dev-documents-cinchy.s3.ap-southeast-2.amazonaws.com/${value}`;
};

const readCompany = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (value.companyLogo || value.companyName || value.tradingName) return value;
  return null;
};

const getNotificationCompany = (item) => {
  const sources = [
    item?.requestedFromCompany,
    item?.requestId?.requestedFromCompany,
    item?.recruiterTransferRequestId?.requestedFromCompany,
    item?.companyTransfer?.requestedFromCompany,
    item?.requestedToCompany,
    item?.requestId?.requestedToCompany,
    item?.recruiterTransferRequestId?.requestedToCompany,
    item?.companyTransfer?.requestedToCompany,
  ];
  const companies = sources.map(readCompany).filter(Boolean);
  return companies.find((company) => company.companyLogo) || companies[0] || null;
};

const NotificationAvatar = ({ item }) => {
  const company = getNotificationCompany(item);
  const logo = resolveCompanyLogo(company?.companyLogo);
  const fallback =
    String(company?.tradingName || company?.companyName || "").trim().charAt(0) ||
    getFirstLetter(item?.message);

  return (
    <Avatar
      src={logo || undefined}
      alt={company?.tradingName || company?.companyName || "Company"}
      style={{
        backgroundColor: "#fde3cf",
        color: "#f56a00",
        textTransform: "capitalize",
      }}
    >
      {fallback}
    </Avatar>
  );
};

const TransferRequestCtas = ({ item, loadingKey, onAction }) => {
  if (!isRecruiterTransferRequest(item)) return null;

  const status = getTransferStatus(item);
  if (status && status !== "pending") {
    return (
      <div className={`notification-transfer-status ${status}`}>
        {status === "accepted"
          ? "Accepted"
          : status === "rejected"
          ? "Rejected"
          : status}
      </div>
    );
  }

  return (
    <div
      className="notification-transfer-actions"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="notification-accept-btn"
        disabled={Boolean(loadingKey)}
        onClick={(e) => onAction(e, item, "accept")}
      >
        {loadingKey === `${item?._id}-accept` ? "..." : "Accept"}
      </button>
      <button
        type="button"
        className="notification-reject-btn"
        disabled={Boolean(loadingKey)}
        onClick={(e) => onAction(e, item, "reject")}
      >
        {loadingKey === `${item?._id}-reject` ? "..." : "Reject"}
      </button>
    </div>
  );
};

const Notification = ({
  isModalOpen,
  showModal,
  handleOk,
  handleCancel,
  viewAllState,
  setviewAllState,
  isMobile,
  setIsMobile,
  notifications,
  setDataChange,
}) => {
  const [open, setOpen] = useState(false);
  const [todayNotification, setTodayNotification] = useState([]);
  const [yesterdayNotification, setYesterdayNotification] = useState([]);
  const [olderNotification, setOlderNotification] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [allNotifications, setAllNotifications] = useState(notifications);
  const [actionLoading, setActionLoading] = useState(null);
  const navigate = useNavigate();
  const showDrawer = () => {
    setOpen(true);
  };
  const onClose = () => {
    setFilterType("all");
    setOpen(false);
    setviewAllState((prev) => !prev);
    /* function calling here to close notification popup when when notification drawer is being closed*/
    handleCancel();
  };

  const setFields = () => {
    let tempTodaysNotification = [];
    let tempYesterdayNotification = [];
    let tempOlderNotification = [];
    allNotifications?.map((item) => {
      if (moment().diff(moment(item?.createdAt), "days") === 0) {
        tempTodaysNotification.push(item);
      } else if (moment().diff(moment(item?.createdAt), "days") === 1) {
        tempYesterdayNotification.push(item);
      } else {
        tempOlderNotification.push(item);
      }
    });
    setTodayNotification(tempTodaysNotification);
    setYesterdayNotification(tempYesterdayNotification);
    setOlderNotification(tempOlderNotification);
  };
  useEffect(() => {
    if (notifications && notifications?.length > 0) {
      setFields();
    }
  }, [allNotifications]);

  useEffect(() => {
    if (filterType === "all") {
      setAllNotifications(notifications);
    } else {
      if (filterType === "read") {
        const temp = notifications?.filter((item) => item?.isRead === true);
        setAllNotifications(temp);
      } else if (filterType === "unread") {
        const temp = notifications?.filter((item) => item?.isRead === false);
        setAllNotifications(temp);
      }
    }
  }, [filterType, notifications]);
  const items = [
    {
      key: "1",
      label: (
        <div className="">
          <Radio.Group
            onChange={(e) => setFilterType(e?.target?.value)}
            className="dropDown_radio_group"
            value={filterType}
          >
            <div className="">
              <Row className="filter_item_row">
                <Col span={24}>
                  <Radio value={"all"}>All</Radio>
                </Col>
              </Row>
              <Row className="filter_item_row">
                <Col span={24}>
                  <Radio value={"unread"}>Unread</Radio>
                </Col>
              </Row>
              <Row className="filter_item_row">
                <Col span={24}>
                  <Radio value={"read"}>Read</Radio>
                </Col>
              </Row>
            </div>
          </Radio.Group>
        </div>
      ),
    },
  ];
  const handleRead = async (item) => {
    // if (
    //   !item?.invitationId?.advertId?._id &&
    //   !item?.applicationId?.advertId?.id &&
    //   !item?.withdrawlBy &&
    //   !item?.newsId &&
    //   !(item?.type === "PRIVACY_POLICY") &&
    //   !(item?.type === "TERMS_CONDITION") &&
    //   !(item?.type === "CUSTOM") &&
    //   !(item?.type === "ADVERT")
    // ) {
    //   message.error("This advert does not exist.");
    //   return;
    // }
    try {
      await API_MANAGER.readNotification(item?._id);

      setDataChange((prev) => !prev);
      if (isRecruiterTransferRequest(item)) {
        return;
      }
      if (item?.type === "PRIVACY_POLICY") {
        navigate("/privacy-policy");
      } else if (item?.type === "TERMS_CONDITION") {
        navigate("/terms-of-use");
      } else if (item?.type === "INVITE" && item?.invitationId?.advertId?._id) {
        navigate(`/advert-detail/${item?.invitationId?.advertId?._id}`);
      } else if (
        (item?.type === "SHORTLISTED" && item?.applicationId?.advertId?.id) ||
        (item?.type === "OFFERED" && item?.applicationId?.advertId?.id)
      ) {
        navigate(`/advert-detail/${item?.applicationId?.advertId?.id}`);
      }
      // else if (
      //   (item?.type === "INVITE" ||
      //     item?.type === "SHORTLISTED" ||
      //     item?.type === "OFFERED") &&
      //   item?.advertId
      // ) {
      //   navigate(`/advert-detail/${item?.advertId}`);
      // }
      else if (item?.type === "ADVERT" && item?.advertId) {
        navigate(`/advert-detail/${item?.advertId}`);
      } else if (item?.type === "NEWS" || item?.newsId) {
        navigate(`/news-feed/${item?.newsId}`);
      } else if (item?.withdrawlBy) {
        navigate(`/applicant-profile/${item?.withdrawlBy}`, {
          state: { withDrawlCandidate: "yes" },
        });
      } else {
        setviewAllState((prev) => true);
      }
    } catch (error) {
      // message.error(error.response.data.message.message)
    }
  };

  const handleTransferAction = async (event, item, action) => {
    event.stopPropagation();
    event.preventDefault();
    const requestId = getTransferRequestId(item);
    if (!requestId) {
      message.error("Transfer request id is missing.");
      return;
    }
    setActionLoading(`${item?._id}-${action}`);
    try {
      await API_MANAGER.respondCompanyTransferRequest(
        requestId,
        action === "accept"
      );
      message.success(
        action === "accept"
          ? "Transfer request accepted."
          : "Transfer request rejected."
      );
      setDataChange((prev) => !prev);
      if (action === "accept") {
        window.dispatchEvent(new CustomEvent("refreshCampaignData"));
      }
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message?.message ||
        error?.response?.data?.message;
      message.error(
        (Array.isArray(apiMessage) ? apiMessage.join(", ") : apiMessage) ||
          "Failed to update transfer request. Please try again."
      );
    } finally {
      setActionLoading(null);
    }
  };
  useEffect(() => {
    if (viewAllState) {
      setOpen(true);
    }
  }, [viewAllState]);
  return (
    <>
      {/* small  notification popup  */}
      {!viewAllState ? (
        <Modal
          open={isModalOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          maskStyle={{ background: "transparent" }}
          footer={[]}
          width="350px"
          closeIcon={false}
          bodyStyle={{
            margin: "-18px",
          }}
          style={{
            position: "absolute",
            top: "70px",
            right: "40px",
          }}
          className="notification-container"
        >
          <div>
            <Row justify={"space-between"} className="Notifications-header">
              <Col className="notification-heading">Notifications</Col>
              {/* {(todayNotification?.length > 0 ||
                yesterdayNotification?.length > 0 ||
                olderNotification?.length > 0) && ( */}
              <Col
                className="viewall-text"
                onClick={() => {
                  showDrawer();
                  setviewAllState((prev) => !prev);
                }}
              >
                View All
              </Col>
              {/* )} */}
            </Row>
            {/* notification cards */}
            {notifications?.length > 0 ? (
              <Col span={24} className="notification-cards-container">
                {notifications?.map((item, i) => (
                  <Row
                    justify={"space-between"}
                    className={`${
                      item?.isRead
                        ? "notification-card-outline"
                        : "notification-card-outline-read"
                    } cursor-pointer`}
                    onClick={() => handleRead(item)}
                  >
                    <Col span={2}>
                      <NotificationAvatar item={item} />
                    </Col>
                    <Col span={21}>
                      <p className="card-text">{renderMessageWithLinks(item?.message, navigate)}</p>
                      <p className="card-time-text">
                        {moment(item?.createdAt).fromNow()}
                      </p>
                      <TransferRequestCtas
                        item={item}
                        loadingKey={actionLoading}
                        onAction={handleTransferAction}
                      />
                    </Col>
                  </Row>
                ))}
              </Col>
            ) : (
              <Col span={24} className="noRecentNotification">
                <Row justify={"center"}>No recent notifications!</Row>
              </Col>
            )}
            {/* <Row
              className="notification-footer"
              justify={"space-between"}
              align={"middle"}
            >
              <Col>
                <span>Notifications</span>
              </Col>
              <Col>
                <Switch defaultChecked onChange={onChange} />
              </Col>
            </Row> */}
          </div>
        </Modal>
      ) : (
        /* Drawer to show all notification*/
        <>
          <Drawer
            title={
              <>
                <span>Notifications</span>
                <Dropdown
                  menu={{ items }}
                  overlayClassName="notificationFilterDropdown"
                >
                  <img
                    className="filter-icon"
                    src={filterIcon}
                    alt="filter icon"
                  />
                </Dropdown>
              </>
            }
            placement="right"
            width={"511px"}
            onClose={onClose}
            open={open}
            bodyStyle={{
              padding: "0px 0px 0px 0px",
              overflow: "hidden",
            }}
            contentWrapperStyle={{
              top: isMobile ? "88px" : "0px",
              width: isMobile ? "100vw" : "511px",
            }}
            className="drawer-container"
          >
            <Col span={24} className="notification-cards-container">
              {todayNotification?.length > 0 && (
                <>
                  <Col span={24}>
                    <p className="day-text">Today</p>
                  </Col>
                  {/* copy and paste */}
                  {todayNotification?.map((item, i) => (
                    <Row
                      justify={"space-between"}
                      className={`${
                        item?.isRead
                          ? "notification-card-outline"
                          : "notification-card-outline-read"
                      } cursor-pointer`}
                      gutter={16}
                      align={"middle"}
                      onClick={() => {
                        handleRead(item);
                      }}
                    >
                      <Col span={2}>
                        <NotificationAvatar item={item} />
                      </Col>
                      <Col span={17}>
                        <p className="card-text">{renderMessageWithLinks(item?.message, navigate)}</p>
                        <TransferRequestCtas
                          item={item}
                          loadingKey={actionLoading}
                          onAction={handleTransferAction}
                        />
                      </Col>
                      <Col span={5}>
                        <span className="card-time-text">
                          {moment(item?.createdAt).fromNow()}
                        </span>
                      </Col>
                    </Row>
                  ))}
                </>
              )}

              {/**************************  Yesterday ********************************/}

              {yesterdayNotification?.length > 0 && (
                <>
                  <Col span={24}>
                    <p className="day-text">Yesterday</p>
                  </Col>
                  {/* copy and paste */}
                  {yesterdayNotification?.map((item, i) => (
                    <Row
                      justify={"space-between"}
                      className={`${
                        item?.isRead
                          ? "notification-card-outline"
                          : "notification-card-outline-read"
                      } cursor-pointer`}
                      onClick={() => {
                        handleRead(item);
                      }}
                    >
                      <Col span={2}>
                        <NotificationAvatar item={item} />
                      </Col>
                      <Col span={18}>
                        <p className="card-text">{renderMessageWithLinks(item?.message, navigate)}</p>
                        <TransferRequestCtas
                          item={item}
                          loadingKey={actionLoading}
                          onAction={handleTransferAction}
                        />
                      </Col>
                      <Col span={3}>
                        <span className="card-time-text">
                          {moment(item?.createdAt).fromNow()}
                        </span>
                      </Col>
                    </Row>
                  ))}
                </>
              )}

              {olderNotification?.length > 0 && (
                <>
                  <Col span={24}>
                    <p className="day-text">Older</p>
                  </Col>
                  {/* copy and paste */}
                  {olderNotification?.map((item, i) => (
                    <Row
                      justify={"space-between"}
                      className={`${
                        item?.isRead
                          ? "notification-card-outline"
                          : "notification-card-outline-read"
                      } cursor-pointer`}
                      onClick={() => {
                        handleRead(item);
                      }}
                    >
                      <Col span={2}>
                        <NotificationAvatar item={item} />
                      </Col>
                      <Col span={18}>
                        <p className="card-text">{renderMessageWithLinks(item?.message, navigate)}</p>
                        <TransferRequestCtas
                          item={item}
                          loadingKey={actionLoading}
                          onAction={handleTransferAction}
                        />
                      </Col>
                      <Col span={3}>
                        <span className="card-time-text">
                          {moment(item?.createdAt).fromNow()}
                        </span>
                      </Col>
                    </Row>
                  ))}
                </>
              )}
              {!(allNotifications?.length > 0) && (
                <Col span={24} className="noRecentNotification">
                  <Row justify={"center"}>No recent notifications!</Row>
                </Col>
              )}
            </Col>
          </Drawer>
        </>
      )}
    </>
  );
};

export default Notification;
